import http from "@/settings/apiSettings";
import AppSettings from "@/settings/appSettings";
import JwtServices from "@/services/JwtServices";

// ID-uri fixe seed-uite de /api/Report/SeedPontajReports (vezi PontajReportSeeder.cs)
const PONTAJ_LUNAR_REPORT_ID = "11111111-1111-1111-1111-111111111101";
const PONTAJ_LUNAR_DESIGN_ID = "11111111-1111-1111-1111-111111111102";
const PONTAJ_ISTORIC_REPORT_ID = "11111111-1111-1111-1111-111111111103";
const PONTAJ_ISTORIC_DESIGN_ID = "11111111-1111-1111-1111-111111111104";

class RU_PontajDataService {
  /** Pontaj lunar (echivalent legacy RU_PontajData) */
  getPontajLunar(data: string, concedii = false, punctLucruId?: string, angajatId?: string): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/GetPontajLunar", {
      params: { data, concedii, punctLucruId, angajatId }
    });
  }

  /** Editeaza o celula de pontaj (o zi) */
  updateCell(id: string, data: any): Promise<any> {
    return http.clientResurseUmaneOdata.patch(`/RU_Pontaj(${id})`, data);
  }

  /** Drepturile utilizatorului pe ecranul de pontaj (ex. CentreCost) — pentru gating UI */
  getDrepturiMele(): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/drepturi-mele");
  }

  /** Verifica daca luna e blocata (salarizare inchisa) */
  getStatus(data: string): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/Status", { params: { data } });
  }

  /** Redeschide luna (RU_PontajDeschideLuna) */
  deschideLuna(data: string): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/DeschideLuna", null, { params: { data } });
  }

  /** Codurile de pontaj (cod -> abreviere X/CM/CO/...) */
  getCoduri(): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/GetCoduri");
  }

  /** Pontaj istoric (snapshot luni inchise) — filtrat pe luna */
  getPontajIstoric(data: string, grupAngajati = -1): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/GetPontajIstoric", { params: { data, grupAngajati } });
  }

  /** Pontaj istoric complet — toate lunile (echivalent legacy FillComplet), paginat server-side */
  getPontajIstoricComplet(skip = 0, take = 100): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/GetPontajIstoricComplet", { params: { skip, take } });
  }

  /** Variabile salarii pentru lookup CodVariabilaSalarii (Componentă Sumă) */
  getVariabileSalarii(): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/GetVariabileSalarii");
  }

  /** Zile lucratoare in luna (+ ore) */
  getZileLucratoare(data: string): Promise<any> {
    return http.clientResurseUmane.get("/Pontaj/ZileLucratoare", { params: { data } });
  }

  /** Reincarca pontajul unui angajat (sterge + regenereaza din contract) */
  reincarcaPontaj(data: string, angajatId: string): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/ReincarcaPontaj", null, { params: { data, angajatId } });
  }

  /** Reincarca pontajul pentru toti angajatii lunii */
  reincarcaPontajToti(data: string): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/ReincarcaPontajToti", null, { params: { data } });
  }

  /** Incarca CM/CO in pontaj (toti sau un angajat) */
  incarcaConcedii(data: string, angajatId?: string): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/IncarcaConcedii", null, { params: { data, angajatId } });
  }

  /** Import pontaj din fisier XLSX (multipart) */
  importXlsx(formData: FormData): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/ImportXlsx", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  /** URL absolut pentru raportul de pontaj (usePrintReport) */
  raportUrl(data: string, angajatId = -1): string {
    return `${AppSettings.API_Url_ResurseUmane}Pontaj/Raport?data=${encodeURIComponent(data)}&angajatId=${angajatId}`;
  }

  /** Creaza/actualizeaza rapoartele de pontaj in ERP_Reports pentru compania curenta (idempotent) */
  seedRapoartePontaj(): Promise<any> {
    return http.clientRapoarte.post("/Report/SeedPontajReports");
  }

  private raportPontajPdf(reportId: string, designId: string, data: string, grupAngajati: number): Promise<any> {
    const body = [
      { Name: "Data", Value: data, Type: "DateTime" },
      { Name: "grup_angajati", Value: grupAngajati.toString(), Type: "Int32" },
      { Name: "Utilizator", Value: JwtServices.GetUsername() ?? "", Type: "String" },
    ];
    return http.clientRapoarte.post(
      `/Report/GetReport?reportID=${reportId}&reportDesignID=${designId}`,
      body,
      { responseType: "blob" }
    );
  }

  /** Raport PDF pontaj lunar (SP RU_PontajRaport, via mecanismul DevExpress din ERP_Reports) */
  raportPontajLunar(data: string, grupAngajati = -1): Promise<any> {
    return this.raportPontajPdf(PONTAJ_LUNAR_REPORT_ID, PONTAJ_LUNAR_DESIGN_ID, data, grupAngajati);
  }

  /** Raport PDF pontaj istoric (SP RU_PontajIstoricSelect) */
  raportPontajIstoric(data: string, grupAngajati = -1): Promise<any> {
    return this.raportPontajPdf(PONTAJ_ISTORIC_REPORT_ID, PONTAJ_ISTORIC_DESIGN_ID, data, grupAngajati);
  }

  /** Persistă selecția de angajați (SAL_IDuri) pentru raportul pe selecție (grup_angajati = -2) */
  setPontajSelectie(angajatiIds: string[]): Promise<any> {
    return http.clientRapoarte.post("/Report/SetPontajSelectie", angajatiIds);
  }

  /** Raport PDF pontaj pentru angajații selectați: persistă selecția, apoi generează cu grup_angajati = -2 */
  async raportPontajSelectie(data: string, angajatiIds: string[]): Promise<any> {
    await this.setPontajSelectie(angajatiIds);
    return this.raportPontajLunar(data, -2);
  }

  /** Raport pontaj pe selectie de angajati (persista selectia, apoi randeaza PDF) */
  raportSelectie(data: string, angajatiIds: string[]): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/RaportSelectie", angajatiIds, {
      params: { data },
      responseType: "blob",
    });
  }

  /** Configurare alocare centru de cost (toti sau angajati specificati) */
  configurareCentruCost(payload: { CapitolId: string; ContId?: string; Toti: boolean; AngajatiIds?: string[] }): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/ConfigurareCentruCost", payload);
  }

  /** Repartizeaza centrele de cost (toti sau un angajat) */
  repartizeazaCentreCost(data: string, angajatId?: string): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/RepartizeazaCentreCost", null, { params: { data, angajatId } });
  }

  /** Sterge repartizarile centrelor de cost (toti sau un angajat) */
  stergeRepartizari(data: string, angajatId?: string): Promise<any> {
    return http.clientResurseUmane.delete("/Pontaj/RepartizariCentreCost", { params: { data, angajatId } });
  }

  /** Genereaza documentul de repartizari centre de cost */
  genereazaDocumentRepartizari(data: string): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/GenerareDocumentRepartizari", null, { params: { data } });
  }

  /** Copiaza formulele componentei de suma de la un angajat sursa la angajatii destinatie */
  copiazaFormule(angajatSursaId: string, angajatiDestIds: string[]): Promise<any> {
    return http.clientResurseUmane.post("/Pontaj/CopiazaFormule", {
      AngajatSursaId: angajatSursaId,
      AngajatiDestIds: angajatiDestIds,
    });
  }
}

export default new RU_PontajDataService();
